/**
 * Servicio de Códigos de Descuento
 * ─────────────────────────────────
 * Capa de comunicación con el backend para el módulo de códigos de descuento.
 * Mientras el backend no exista se usan mocks en memoria para que la UI
 * funcione de forma completa e independiente.
 *
 * Cuando el backend esté listo, basta con descomentar las llamadas reales
 * y eliminar el bloque de mocks.
 */

import axios from "axios";
import { getUsertoken } from "@/utils/auth";

const API_URL = process.env.VUE_APP_BASE_URL || "http://localhost:3000/api/v1";

/** Helper: headers con token */
function authHeaders() {
  const token = getUsertoken();
  return { headers: { Authorization: `Bearer ${token}` } };
}

/** Mapea datos del frontend al formato de la API */
function mapToApiFormat(data) {
  const tipoMap = {
    "GENERAL": "general",
    "CUSTOMER": "cliente",
    "SELLER": "vendedor"
  };
  
  const mapped = {
    codigo: data.code,
    descripcion: data.description,
    tipo: tipoMap[data.type] || "general",
    tipo_descuento: data.discountType === "PERCENT" ? "porcentaje" : "monto_fijo",
    valor_descuento: data.discountValue,
    fecha_inicio: data.startDate ? data.startDate.split('T')[0] : data.startDate,
    estado: data.status === "ACTIVE" ? "activo" : data.status === "INACTIVE" ? "inactivo" : "activo"
  };

  if (data.endDate) {
    mapped.fecha_fin = data.endDate.split('T')[0];
  }

  if (data.minOrderEnabled) {
    mapped.requiere_monto_minimo = 1;
    mapped.monto_minimo = data.minOrderAmount;
  } else {
    mapped.requiere_monto_minimo = 0;
  }

  // Se mandan SIEMPRE los dos campos, incluso en 0 o null.
  // El backend actualiza con Sequelize .update(datosActualizar), que solo toca
  // las claves presentes en el payload: si se omite un campo, la base conserva
  // el valor anterior. Al omitir uso_unico se rompian los codigos que pasaban
  // de "uso unico" a "multiples usos": quedaban con uso_unico=1 y
  // limite_usos_total=2, y la validacion corta por uso_unico antes de mirar
  // el limite, dejando el codigo inservible despues del primer uso.
  // Por lo mismo limite_usos_total se manda en null cuando es ilimitado:
  // omitirlo dejaba el limite anterior guardado en la base.
  if (data.usageMode === "SINGLE_USE") {
    mapped.uso_unico = 1;
    mapped.limite_usos_total = 1;
  } else {
    mapped.uso_unico = 0;
    mapped.limite_usos_total = data.maxUsesTotal || null;
  }

  if (data.type === "CUSTOMER") {
    mapped.id_cliente = data.customerId;
    if (data.maxUsesPerMonth) {
      mapped.usos_por_mes = data.maxUsesPerMonth;
    }
  }

  if (data.type === "SELLER") {
    mapped.id_vendedor = data.sellerId;
    mapped.tipo_comision = data.commissionType === "PERCENT" ? "porcentaje" : "monto_fijo";
    mapped.valor_comision = data.commissionValue;
  }

  return mapped;
}

/** Mapea datos de la API al formato del frontend */
function mapFromApiFormat(data) {
  const tipoFromApiMap = {
    "general": "GENERAL",
    "cliente": "CUSTOMER",
    "vendedor": "SELLER"
  };
  
  return {
    id: data.id_codigo || data.id,
    code: data.codigo,
    type: tipoFromApiMap[data.tipo] || "GENERAL",
    description: data.descripcion,
    status: data.estado?.toUpperCase() || "ACTIVE",
    startDate: data.fecha_inicio,
    endDate: data.fecha_fin || null,
    discountType: data.tipo_descuento === "porcentaje" ? "PERCENT" : "FIXED",
    discountValue: parseFloat(data.valor_descuento),
    minOrderEnabled: data.requiere_monto_minimo === 1,
    minOrderAmount: data.monto_minimo || null,
    usageMode: data.uso_unico === 1 ? "SINGLE_USE" : "MULTI_USE",
    maxUsesTotal: data.limite_usos_total || null,
    usesCount: data.usos_realizados || data.usesCount || 0,
    customerId: data.id_cliente || null,
    customerName: data.cliente?.nombre_completo || data.nombre_cliente || null,
    sellerId: data.id_vendedor || null,
    sellerName: data.vendedor?.nombre_completo || data.nombre_vendedor || null,
    maxUsesPerMonth: data.usos_por_mes || null,
    commissionType: data.tipo_comision === "porcentaje" ? "PERCENT" : data.tipo_comision === "monto_fijo" ? "FIXED" : null,
    commissionValue: data.valor_comision ? parseFloat(data.valor_comision) : null,
    createdAt: data.fecha_creacion || data.createdAt,
    updatedAt: data.fecha_actualizacion || data.updatedAt
  };
}

// ═══════════════════════════════════════════════════════════════
//  MOCK DATA — Datos de prueba en memoria
// ═══════════════════════════════════════════════════════════════

let nextId = 100;

/** Genera un código alfanumérico aleatorio */
function generateCode(length = 8) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

const mockCodes = [
  {
    id: 1,
    code: "BIENVENIDO20",
    type: "GENERAL",
    description: "20% de descuento para nuevos usuarios del e-commerce",
    status: "ACTIVE",
    startDate: "2026-01-01T00:00:00.000Z",
    endDate: "2026-12-31T23:59:59.000Z",
    discountType: "PERCENT",
    discountValue: 20,
    minOrderEnabled: true,
    minOrderAmount: 5000,
    usageMode: "MULTI_USE",
    maxUsesTotal: 500,
    usesCount: 123,
    customerId: null,
    customerName: null,
    sellerId: null,
    sellerName: null,
    maxUsesPerMonth: null,
    commissionType: null,
    commissionValue: null,
    createdAt: "2026-01-01T10:00:00.000Z",
    updatedAt: "2026-02-01T15:30:00.000Z",
  },
  {
    id: 2,
    code: "VIP-MARIA",
    type: "CUSTOMER",
    description: "Descuento fijo $1500 para cliente Maria López",
    status: "ACTIVE",
    startDate: "2026-02-01T00:00:00.000Z",
    endDate: null,
    discountType: "FIXED",
    discountValue: 1500,
    minOrderEnabled: true,
    minOrderAmount: 10000,
    usageMode: "MULTI_USE",
    maxUsesTotal: null,
    usesCount: 5,
    customerId: 42,
    customerName: "Maria López",
    sellerId: null,
    sellerName: null,
    maxUsesPerMonth: 2,
    commissionType: null,
    commissionValue: null,
    createdAt: "2026-02-01T08:00:00.000Z",
    updatedAt: "2026-02-10T12:00:00.000Z",
  },
  {
    id: 3,
    code: "AFILIADO-JUAN",
    type: "SELLER",
    description: "Código de afiliado Juan Pérez — 10% dto + 5% comisión",
    status: "ACTIVE",
    startDate: "2026-01-15T00:00:00.000Z",
    endDate: "2026-06-30T23:59:59.000Z",
    discountType: "PERCENT",
    discountValue: 10,
    minOrderEnabled: false,
    minOrderAmount: null,
    usageMode: "MULTI_USE",
    maxUsesTotal: null,
    usesCount: 47,
    customerId: null,
    customerName: null,
    sellerId: 10,
    sellerName: "Juan Pérez",
    maxUsesPerMonth: null,
    commissionType: "PERCENT",
    commissionValue: 5,
    createdAt: "2026-01-15T09:00:00.000Z",
    updatedAt: "2026-02-08T18:00:00.000Z",
  },
  {
    id: 4,
    code: "UNICOUSO50",
    type: "GENERAL",
    description: "50% descuento — código de un solo uso",
    status: "INACTIVE",
    startDate: "2026-03-01T00:00:00.000Z",
    endDate: "2026-03-31T23:59:59.000Z",
    discountType: "PERCENT",
    discountValue: 50,
    minOrderEnabled: false,
    minOrderAmount: null,
    usageMode: "SINGLE_USE",
    maxUsesTotal: 1,
    usesCount: 0,
    customerId: null,
    customerName: null,
    sellerId: null,
    sellerName: null,
    maxUsesPerMonth: null,
    commissionType: null,
    commissionValue: null,
    createdAt: "2026-02-10T10:00:00.000Z",
    updatedAt: "2026-02-10T10:00:00.000Z",
  },
];

const mockUsages = [
  { id: 1, discountCodeId: 1, date: "2026-02-09T14:22:00.000Z", orderId: "PED-1020", customerName: "Carlos Ruiz", orderAmount: 12500, discountApplied: 2500, sellerName: null, commissionGenerated: null },
  { id: 2, discountCodeId: 1, date: "2026-02-08T10:15:00.000Z", orderId: "PED-1018", customerName: "Ana García", orderAmount: 8000, discountApplied: 1600, sellerName: null, commissionGenerated: null },
  { id: 3, discountCodeId: 3, date: "2026-02-07T16:45:00.000Z", orderId: "PED-1015", customerName: "Laura Díaz", orderAmount: 20000, discountApplied: 2000, sellerName: "Juan Pérez", commissionGenerated: 1000 },
  { id: 4, discountCodeId: 3, date: "2026-02-06T09:30:00.000Z", orderId: "PED-1012", customerName: "Pedro Sánchez", orderAmount: 15000, discountApplied: 1500, sellerName: "Juan Pérez", commissionGenerated: 750 },
  { id: 5, discountCodeId: 2, date: "2026-02-05T11:00:00.000Z", orderId: "PED-1010", customerName: "Maria López", orderAmount: 18000, discountApplied: 1500, sellerName: null, commissionGenerated: null },
];

const mockCustomers = [
  { id: 42, name: "Maria López", dni: "30123456", phone: "1155667788" },
  { id: 43, name: "Carlos Ruiz", dni: "28654321", phone: "1144556677" },
  { id: 44, name: "Ana García", dni: "33987654", phone: "1133445566" },
  { id: 45, name: "Laura Díaz", dni: "31456789", phone: "1122334455" },
  { id: 46, name: "Pedro Sánchez", dni: "29111222", phone: "1166778899" },
];

const mockSellers = [
  { id: 10, name: "Juan Pérez", email: "juan@mail.com", status: "ACTIVE" },
  { id: 11, name: "Romina Torres", email: "romina@mail.com", status: "ACTIVE" },
  { id: 12, name: "Diego Martínez", email: "diego@mail.com", status: "ACTIVE" },
];

/** Simula latencia de red */
function delay(ms = 400) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ═══════════════════════════════════════════════════════════════
//  SERVICE CLASS
// ═══════════════════════════════════════════════════════════════

class DiscountCodesService {
  // ─── Listado con filtros ───────────────────────────────────
  async getAll(params = {}) {
    const response = await axios.get(`${API_URL}/codigos-descuento`, {
      ...authHeaders(),
      params,
    });
    
    if (response.data.success && response.data.data && Array.isArray(response.data.data.codigos)) {
      return {
        data: response.data.data.codigos.map(mapFromApiFormat),
        total: response.data.data.total || response.data.data.codigos.length
      };
    }
    return { data: [], total: 0 };
  }

  // ─── Detalle por ID ────────────────────────────────────────
  async getById(id) {
    const response = await axios.get(`${API_URL}/codigos-descuento/${id}`, authHeaders());
    return { data: mapFromApiFormat(response.data.data || response.data) };
  }

  // ─── Crear ─────────────────────────────────────────────────
  async create(payload) {
    const apiPayload = mapToApiFormat(payload);
    const response = await axios.post(`${API_URL}/codigos-descuento`, apiPayload, authHeaders());
    return { data: mapFromApiFormat(response.data.data || response.data) };
  }

  // ─── Actualizar ────────────────────────────────────────────
  async update(id, payload) {
    const apiPayload = mapToApiFormat(payload);
    const response = await axios.put(`${API_URL}/codigos-descuento/${id}`, apiPayload, authHeaders());
    return { data: mapFromApiFormat(response.data.data || response.data) };
  }

  // ─── Cambiar estado (activar/desactivar) ───────────────────
  async updateStatus(id, status) {
    const estado = status === "ACTIVE" ? "activo" : "inactivo";
    const response = await axios.put(`${API_URL}/codigos-descuento/${id}`, { estado }, authHeaders());
    return { data: mapFromApiFormat(response.data.data || response.data) };
  }

  // ─── Eliminar ──────────────────────────────────────────────
  async remove(id) {
    const response = await axios.delete(`${API_URL}/codigos-descuento/${id}`, authHeaders());
    return response.data;
  }

  // ─── Historial de usos ────────────────────────────────────
  async getUsages(id, params = {}) {
    const response = await axios.get(`${API_URL}/codigos-descuento/${id}/historial`, {
      ...authHeaders(),
      params,
    });
    return response.data;
  }

  // ─── Buscar clientes (autocomplete) ───────────────────────
  async searchCustomers(search = "") {
    await delay(200);
    if (!search) return { data: mockCustomers };
    const s = search.toLowerCase();
    const filtered = mockCustomers.filter(
      (c) => c.name.toLowerCase().includes(s) || c.dni.includes(s)
    );
    return { data: filtered };

    // const response = await axios.get(`${API_URL}clientes`, {
    //   ...authHeaders(),
    //   params: { search },
    // });
    // return response.data;
  }

  // ─── Buscar vendedores (autocomplete) ─────────────────────
  async searchSellers(search = "") {
    await delay(200);
    if (!search) return { data: mockSellers };
    const s = search.toLowerCase();
    const filtered = mockSellers.filter((v) =>
      v.name.toLowerCase().includes(s)
    );
    return { data: filtered };

    // const response = await axios.get(`${API_URL}vendedores`, {
    //   ...authHeaders(),
    //   params: { search },
    // });
    // return response.data;
  }

  // ─── Generar código aleatorio ─────────────────────────────
  generateCode(length = 8) {
    return generateCode(length);
  }

  // ─── Validar código de descuento ──────────────────────────
  async validateCode(payload) {
    const response = await axios.post(`${API_URL}/codigos-descuento/validar`, payload);
    return response.data;
  }

  // ─── Registrar uso de código ──────────────────────────────
  async registerUsage(payload) {
    const response = await axios.post(`${API_URL}/codigos-descuento/registrar-uso`, payload, authHeaders());
    return response.data;
  }

  // ─── Obtener estadísticas de un código ───────────────────
  async getStatistics(id) {
    const response = await axios.get(`${API_URL}/codigos-descuento/${id}/estadisticas`, authHeaders());
    return response.data;
  }
}

export default new DiscountCodesService();
